import React, { useMemo } from "react";
import { Img, Heading, Text } from "../../components";
import { useNavigate } from "react-router-dom";

/** helpers */
const pick = (obj, paths, fallback = null) => {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;
    for (const k of parts) {
      if (cur && Object.prototype.hasOwnProperty.call(cur, k)) cur = cur[k];
      else {
        ok = false;
        break;
      }
    }
    if (ok && cur !== null && cur !== undefined && cur !== "") return cur;
  }
  return fallback;
};

const parseNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[^\d.-]/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const formatMoney = (val) => {
  const n = parseNumber(val);
  if (n === null) return "---";
  return `₪ ${n.toLocaleString("he-IL")}`;
};

const getMyOfferVal = (n) =>
  pick(
    n,
    [
      "my_offer_price",
      "my_price",
      "offered_price",
      "offer_price",
      "data.my_offer_price",
      "data.offered_price",
    ],
    null
  );

const getApprovedOfferVal = (n) => {
  const rawType = String(pick(n, ["type", "notification_type", "type_text"], "")).toLowerCase();
  const isApprovedOther =
    rawType.includes("offer_approved_other") ||
    Number(pick(n, ["offer_approved_other", "data.offer_approved_other"], 0)) === 1;

  if (isApprovedOther) {
    return pick(
      n,
      [
        "approved_other_price",
        "winner_offer_price",
        "approved_offer_price",
        "approved_price",
        "accepted_price",
        "data.approved_other_price",
        "data.winner_offer_price",
        "data.approved_price",
      ],
      null
    );
  }

  return pick(
    n,
    [
      "approved_offer_price",
      "approved_price",
      "accepted_price",
      "winner_offer_price",
      "data.approved_offer_price",
      "data.approved_price",
      "data.accepted_price",
      // fallback אם אין שדה ייעודי
      "my_offer_price",
      "offered_price",
      "offer_price",
      "data.my_offer_price",
      "data.offered_price",
    ],
    null
  );
};

const getCarIdFromNotif = (n) =>
  pick(
    n,
    ["car_id", "ad_id", "entity_id", "object_id", "data.car_id", "data.ad_id"],
    null
  );

const SeenBadge = ({ isRead, onClick }) => {
  const bg = isRead ? "rgb(237, 255, 216)" : "rgb(255, 233, 233)";
  const clr = isRead ? "rgb(119, 172, 59)" : "rgb(214, 31, 31)";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center cursor-pointer transition"
      style={{ backgroundColor: bg }}
      title={isRead ? "נצפה" : "סמן כנצפה"}
      aria-label={isRead ? "נצפה" : "סמן כנצפה"}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path
          d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
          stroke={clr}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="2.5" stroke={clr} strokeWidth="2" />
      </svg>
    </button>
  );
};

export default function NotificationCard({ notification, onClose }) {
  const navigate = useNavigate();

  const {
    manufacturer,
    model,
    sub_model,
    year,
    created_at,
    type,
  } = notification;

  const isRead = Number(pick(notification, ["is_read", "read", "data.is_read"], 0)) === 1;

  // שם רכב
  const carDisplay = [manufacturer, model, sub_model, year].filter(Boolean).join(" ");

  // מחירים (מה שחסר)
  const myOfferText = useMemo(
    () => formatMoney(getMyOfferVal(notification)),
    [notification]
  );
  const approvedOfferText = useMemo(
    () => formatMoney(getApprovedOfferVal(notification)),
    [notification]
  );

  // תאריך
  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("he-IL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";

  const titles = {
    offer_approved: "!זכית במודעת רכב",
    offer_approved_other: "הצעה אחרת אושרה",
    new_offer: "!קיבלת הצעה חדשה",
    immediate_purchase_approved: "רכישה מידית",
    immediate_purchase_closed_other: "ההצעה שלך נדחתה",
  };

  const carId = getCarIdFromNotif(notification);

  const markRead = async () => {
    if (isRead) return;
    if (onClose) await onClose(); // זה אצלך עושה mark-read + עדכון UI
  };

  const openAd = async () => {
    if (!isRead) await markRead();
    if (carId) navigate(`/dealer-cars/${carId}`);
  };

  return (
    <>
      {/* ✅ במקום X: עין + סימון נקראה */}
      <div className="absolute top-3 left-3">
        <SeenBadge isRead={isRead} onClick={markRead} />
      </div>

      <div className="flex items-start gap-4 flex-row-reverse text-right">
        <Img src="/images/gold-cup.svg" alt="גביע" className="w-20 h-20" />

        <div className="flex-1 flex flex-col text-right">
          <Heading as="h6" className="font-bold text-red-600 mb-1">
            {titles[type] ?? "התראה"}
          </Heading>

          {/* רכב */}
          {carDisplay && (
            <div className="flex items-center justify-end gap-1">
              <Text className="font-medium text-gray-900">{carDisplay}</Text>
              <Text className="text-gray-700">:רכב</Text>
            </div>
          )}

          {/* ✅ מחיר הצעה שלי */}
          <div className="flex items-center justify-end gap-1 mt-1">
            <Text className="font-medium text-gray-900">{myOfferText}</Text>
            <Text className="text-gray-700">מחיר הצעה שלי:</Text>
          </div>

          {/* ✅ מחיר הצעה שאושרה */}
          <div className="flex items-center justify-end gap-1 mt-1">
            <Text className="font-medium text-gray-900">{approvedOfferText}</Text>
            <Text className="text-gray-700">מחיר הצעה שאושרה:</Text>
          </div>

          {/* ✅ לצפיה במודעה */}
          {!!carId && (
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={openAd}
                className="flex items-center gap-2 rounded-[10px] px-3 py-2 bg-white border border-gray-200"
              >     <img
                  className="h-[12px]"
                  src="/images/img_settings_black_900.svg"
                  alt="Settings"
                />
                <span className="text-black-900_03 font-assistant text-[14px] font-semibold">
                  לצפיה במודעה
                </span>
           
              </button>
            </div>
          )}

  
        </div>
      </div>
    </>
  );
}
